/**
 * Pin my view: the control every page of draggable cards shares. Pinning holds
 * the user's arrangement still; a one-time tip teaches the drag.
 *
 * A page adds it in three lines:
 *   const cardsView = usePinnedView('transfer-detail')
 *   <PinMyViewBar view={cardsView} />
 *   <DraggableCardGrid ... locked={cardsView.pinned}>
 */

import { GripVertical, Lock, LockOpen, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/components/ui/button'
import type { PinnedView } from '@/admin/hooks/use-pinned-view'

export function PinMyViewBar({ view }: { view: PinnedView }) {
  const { t } = useTranslation()
  return (
    <div className="mb-3 flex items-center justify-between gap-3" data-testid="pin-my-view-bar">
      {/* 14px at normal weight on a rose chip: at 11px light the tip went
          unread by older users, and a clipped instruction is no instruction. */}
      {!view.hintSeen && !view.pinned ? (
        <span className="flex min-w-0 items-start gap-2.5 rounded-[6px] border-[0.5px] border-[var(--rose-border)] bg-[var(--rose-bg)] px-3.5 py-2.5 text-[14px] font-normal leading-[1.45] text-[var(--rose)]">
          <GripVertical className="mt-[1px] h-4 w-4 shrink-0" />
          <span className="min-w-0">
            {t(
              'detailCards.arrangeHint',
              'This page is yours to arrange. Move cards into the order you work in, then pin your favourite layout to keep it.',
            )}
          </span>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('detailCards.dismissHint', 'Dismiss this tip')}
            onClick={view.dismissHint}
            className="-my-1 shrink-0 text-[var(--rose)] hover:bg-[var(--rose-bg)] hover:text-[var(--rose-hover)]"
          >
            <X className="h-4 w-4" />
          </Button>
        </span>
      ) : (
        <span />
      )}
      <Button
        variant="ghost"
        size="xs"
        onClick={view.togglePin}
        aria-pressed={view.pinned}
        data-testid="pin-my-view"
        className="h-auto shrink-0 px-0 text-[11px] text-[var(--green)]"
      >
        {view.pinned ? (
          <>
            <Lock className="mr-1 h-3 w-3" />
            {t('detailCards.viewPinned', 'View pinned')}
          </>
        ) : (
          <>
            <LockOpen className="mr-1 h-3 w-3" />
            {t('detailCards.pinView', 'Pin my view')}
          </>
        )}
      </Button>
    </div>
  )
}
